import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PageTitle from '../../components/PageTitle';
import MemberCard from '../../components/MemberCard';
import { getEffectiveRoleForSorting, getRankScore } from '../../utils/rank';
import api from '../../services/api';

interface Member {
  id: string;
  fullName: string;
  photo: string;
  memberType: string;
  rank: string;
  memberStatus: string;
  currentState: string;
  designation: string;
  district: string;
  branch: string;
}

const container = {
  hidden: { opacity: 1, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.2
    }
  }
};

const District = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const response = await api.getPublicMembers('District', 'Active');
        
        const allMembers = (response.members || [])
          .map((data: any) => ({
            id: data.id || data.user_id,
            fullName: data.full_name || 'Unknown',
            photo: data.photo || '/default-avatar.png',
            memberType: data.member_type || 'Member',
            rank: data.rank || 'Member',
            memberStatus: data.member_status || 'Active',
            currentState: data.current_state || 'Unknown',
            designation: data.designation || 'President',
            district: data.current_district || 'Unknown',
            branch: data.branch || ''
          }));

        const membersData = allMembers.sort((a: Member, b: Member) => {
          const aScore = getRankScore(getEffectiveRoleForSorting(a.rank, a.designation));
          const bScore = getRankScore(getEffectiveRoleForSorting(b.rank, b.designation));
          if (aScore !== bScore) return aScore - bScore;
          return a.fullName.localeCompare(b.fullName);
        });

        setMembers(membersData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching members:', error);
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  return (
    <div className="bg-gray-900 min-h-screen">
      <PageTitle
        title="District Members"
        subtitle="View all district level members"
      />

      <div className="container mx-auto px-4 py-8 sm:py-12">
        {loading ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center text-gray-400 text-sm sm:text-base">
            No members found
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8"
          >
            {members.map(member => (
              <MemberCard
                key={member.id}
                name={member.fullName}
                photoUrl={member.photo}
                memberType={`${member.memberType} ${member.designation || 'President'}`}
                rank={''}
                state={`${member.district || 'Unknown'}, ${member.currentState}`}
                branch={member.branch}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default District;
